import { useEffect, useRef, useState } from "react";
import { useRealtime } from "@/lib/realtime-context";
import { getDashboardActivity } from "@/lib/dashboard";
import { fmtRelative } from "@/lib/format";
import {
  ShoppingBag, RotateCcw, Wrench, Truck, CreditCard, Activity,
  Receipt, Package, AlertTriangle, RefreshCw, Users, Lock, CheckCircle, XCircle, Boxes,
} from "lucide-react";
import { cn } from "@/lib/utils";

// Local humanizer — mirrors backend EVENT_META so a live SSE event renders
// identically to a persisted one before it's reloaded from /activity.
const TOPIC_META = {
  "order.created":       { Icon: ShoppingBag,    accent: "emerald" },
  "order.updated":       { Icon: ShoppingBag,    accent: "sky" },
  "order.cancelled":     { Icon: ShoppingBag,    accent: "rose" },
  "fulfillment.created": { Icon: Truck,          accent: "violet" },
  "fulfillment.updated": { Icon: Truck,          accent: "sky" },
  "refund.created":      { Icon: CreditCard,     accent: "rose" },
  "return.created":      { Icon: RotateCcw,      accent: "amber" },
  "return.updated":      { Icon: RotateCcw,      accent: "sky" },
  "return.approved":     { Icon: CheckCircle,    accent: "emerald" },
  "return.rejected":     { Icon: XCircle,        accent: "rose" },
  "service.created":     { Icon: Wrench,         accent: "violet" },
  "service.updated":     { Icon: Wrench,         accent: "sky" },
  "awb.generated":       { Icon: Truck,          accent: "violet" },
  "awb.cancelled":       { Icon: Truck,          accent: "rose" },
  "shipment.picked_up":  { Icon: Package,        accent: "sky" },
  "shipment.delivered":  { Icon: CheckCircle,    accent: "emerald" },
  "shipment.refused":    { Icon: XCircle,        accent: "rose" },
  "invoice.generated":   { Icon: Receipt,        accent: "emerald" },
  "invoice.cancelled":   { Icon: Receipt,        accent: "rose" },
  "stock.critical":      { Icon: AlertTriangle,  accent: "rose" },
  "stock.adjusted":      { Icon: Boxes,          accent: "sky" },
  "reconciliation.completed": { Icon: RefreshCw, accent: "violet" },
  "app.uninstalled":     { Icon: AlertTriangle,  accent: "rose" },
  "users.created":       { Icon: Users,          accent: "sky" },
  "users.updated":       { Icon: Users,          accent: "sky" },
  "users.deleted":       { Icon: Users,          accent: "rose" },
  "users.permissions_changed": { Icon: Lock,     accent: "amber" },
};

const COLORS = {
  emerald: { bg: "bg-emerald-500/10", text: "text-emerald-600" },
  sky:     { bg: "bg-sky-500/10",     text: "text-sky-600" },
  rose:    { bg: "bg-rose-500/10",    text: "text-rose-600" },
  amber:   { bg: "bg-amber-500/10",   text: "text-amber-600" },
  violet:  { bg: "bg-violet-500/10",  text: "text-violet-600" },
  zinc:    { bg: "bg-zinc-100",       text: "text-zinc-600" },
};

export default function ActivityFeed() {
  const { subscribe, status } = useRealtime();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const seenRef = useRef(new Set());

  // Initial load + poll fallback every 30s
  useEffect(() => {
    let alive = true;
    let interval;
    const load = async () => {
      try {
        const data = await getDashboardActivity(100);
        if (!alive) return;
        seenRef.current = new Set(data.map((d) => `${d.topic}|${d.created_at}|${d.title}`));
        setItems(data);
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    interval = setInterval(load, 30000);
    return () => { alive = false; clearInterval(interval); };
  }, []);

  // Live append on SSE topics we care about
  useEffect(() => {
    const topics = Object.keys(TOPIC_META);
    const offs = topics.map((t) =>
      subscribe(t, (evt) => {
        const meta = TOPIC_META[t];
        if (!meta) return;
        const entry = {
          topic: t,
          type: t.split(".")[0],
          title: humanTitle(t, evt?.data),
          detail: humanDetail(t, evt?.data),
          data: evt?.data,
          created_at: evt?.at || new Date().toISOString(),
        };
        const key = `${entry.topic}|${entry.created_at}|${entry.title}`;
        if (seenRef.current.has(key)) return;
        seenRef.current.add(key);
        setItems((prev) => [entry, ...prev].slice(0, 100));
      }),
    );
    return () => offs.forEach((off) => off && off());
  }, [subscribe]);

  return (
    <div className="rounded-xl border border-border bg-white" data-testid="activity-feed">
      <div className="flex items-center justify-between px-4 h-12 border-b border-border">
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4 text-muted-foreground" />
          <h3 className="font-display font-semibold text-sm">Live activity feed</h3>
        </div>
        <span
          className={cn(
            "text-[10px] font-medium uppercase tracking-widest",
            status === "live" ? "text-emerald-600" : "text-muted-foreground",
          )}
        >
          {status === "live" ? "● Live" : "○ Conectare…"}
        </span>
      </div>
      <div className="divide-y divide-border max-h-[560px] overflow-y-auto">
        {loading && Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="px-4 py-3 animate-pulse">
            <div className="h-3 w-2/3 bg-zinc-200 rounded" />
            <div className="h-2 w-1/2 bg-zinc-100 rounded mt-2" />
          </div>
        ))}
        {!loading && items.length === 0 && (
          <div className="px-4 py-12 text-center text-sm text-muted-foreground">
            Activitatea va apărea aici în timp real.
          </div>
        )}
        {!loading && items.map((it, i) => {
          const meta = TOPIC_META[it.topic] || { Icon: Activity, accent: "zinc" };
          const c = COLORS[meta.accent] || COLORS.zinc;
          const title = it.title || humanTitle(it.topic, it.data);
          const detail = it.detail || humanDetail(it.topic, it.data);
          return (
            <div key={`${it.topic}-${it.created_at}-${i}`} className="px-4 py-3 flex items-start gap-3 hover:bg-zinc-50 transition" data-testid={`activity-item-${i}`}>
              <div className={cn("h-7 w-7 rounded-md flex items-center justify-center shrink-0", c.bg)}>
                <meta.Icon className={cn("h-3.5 w-3.5", c.text)} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium leading-snug truncate">{title}</div>
                {detail && <div className="text-xs text-muted-foreground mt-0.5 truncate">{detail}</div>}
              </div>
              <div className="text-[11px] text-muted-foreground tabular-nums shrink-0 mt-0.5">
                {fmtRelative(it.created_at)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function humanTitle(topic, d) {
  d = d || {};
  if (topic === "order.created") return `Comanda #${d.order_number || d.name || d.id || ""} importată din Shopify`.trim();
  if (topic === "order.updated") return `Comandă actualizată · #${d.order_number || d.id || ""}`.trim();
  if (topic === "order.cancelled") return `Comandă anulată · #${d.order_number || d.id || ""}`.trim();
  if (topic === "fulfillment.created") return `Expediere creată · #${d.order_number || d.order_id || ""}`.trim();
  if (topic === "fulfillment.updated") return `Expediere actualizată · #${d.order_number || d.order_id || ""}`.trim();
  if (topic === "refund.created") return `Rambursare · #${d.order_number || d.order_id || ""}`.trim();
  if (topic === "return.created") return `Retur nou creat · ${d.return_number || d.id || ""}`.trim();
  if (topic === "return.updated") return `Retur actualizat · ${d.return_number || d.id || ""}`.trim();
  if (topic === "return.approved") return `Retur aprobat · ${d.return_number || d.id || ""}`.trim();
  if (topic === "return.rejected") return `Retur respins · ${d.return_number || d.id || ""}`.trim();
  if (topic === "service.created") return `Cerere service nouă · ${d.service_number || d.id || ""}`.trim();
  if (topic === "service.updated") return `Service actualizat · ${d.service_number || d.id || ""}`.trim();
  if (topic === "awb.generated") return `AWB GLS generat${d.order_number ? ` pentru comanda #${d.order_number}` : ""}`;
  if (topic === "awb.cancelled") return `AWB anulat${d.awb ? ` · ${d.awb}` : ""}`;
  if (topic === "shipment.picked_up") return `Colet ridicat${d.awb ? ` · AWB ${d.awb}` : ""}`;
  if (topic === "shipment.delivered") return `Colet livrat${d.awb ? ` · AWB ${d.awb}` : ""}`;
  if (topic === "shipment.refused") return `Colet refuzat${d.awb ? ` · AWB ${d.awb}` : ""}`;
  if (topic === "invoice.generated") return `Factură emisă${d.order_number ? ` pentru comanda #${d.order_number}` : ""}`;
  if (topic === "invoice.cancelled") return `Factură anulată${d.invoice_number ? ` · ${d.invoice_number}` : ""}`;
  if (topic === "stock.critical") return `Stoc critic detectat${d.product_title ? ` · ${d.product_title}` : ""}`;
  if (topic === "stock.adjusted") return `Stoc ajustat${d.sku ? ` · ${d.sku}` : ""}`;
  if (topic === "reconciliation.completed") return `Reconciliere finalizată${d.count != null ? ` · ${d.count} comenzi procesate` : ""}`;
  if (topic === "users.created") return `Utilizator nou · ${d.email || d.name || ""}`;
  if (topic === "users.updated") return `Utilizator actualizat · ${d.email || d.name || ""}`;
  if (topic === "users.deleted") return `Utilizator șters`;
  if (topic === "users.permissions_changed") return `Permisiuni modificate${d.email ? ` · ${d.email}` : ""}`;
  if (topic === "app.uninstalled") return `App Shopify dezinstalată · ${d.shop || ""}`;
  return topic;
}
function humanDetail(topic, d) {
  d = d || {};
  const parts = [];
  if (d.customer_name) parts.push(d.customer_name);
  if (d.order_number && topic.startsWith("return")) parts.push(`comanda #${d.order_number}`);
  if (d.email && !d.customer_name) parts.push(d.email);
  return parts.length ? parts.join(" · ") : null;
}
